import * as React from "react";

const COLOR = "__icon_color__";

type Shape =
  | { kind: "path"; d: string; fill?: string; stroke?: string; strokeWidth?: number }
  | { kind: "circle"; cx: number; cy: number; r: number; fill?: string; stroke?: string; strokeWidth?: number }
  | { kind: "line"; x1: number; y1: number; x2: number; y2: number; stroke?: string; strokeWidth?: number }
  | { kind: "rect"; x: number; y: number; width: number; height: number; rx?: number; fill?: string; stroke?: string; strokeWidth?: number };

type IconDefinition = { shapes: Shape[] };

export type IoniconsProps = {
  name: string;
  size?: number;
  color?: string;
  style?: unknown;
  accessible?: boolean;
  accessibilityLabel?: string;
  accessibilityRole?: string;
  testID?: string;
  [key: string]: unknown;
};

const path = (d: string, options: Omit<Extract<Shape, { kind: "path" }>, "kind" | "d"> = {}): Shape => ({
  kind: "path",
  d,
  fill: "none",
  stroke: COLOR,
  strokeWidth: 2,
  ...options,
});

const filledPath = (d: string): Shape => ({ kind: "path", d, fill: COLOR, stroke: "none" });
const circle = (cx: number, cy: number, r: number, options: Omit<Extract<Shape, { kind: "circle" }>, "kind" | "cx" | "cy" | "r"> = {}): Shape => ({
  kind: "circle",
  cx,
  cy,
  r,
  fill: "none",
  stroke: COLOR,
  strokeWidth: 2,
  ...options,
});
const filledCircle = (cx: number, cy: number, r: number, fill = COLOR): Shape => ({ kind: "circle", cx, cy, r, fill, stroke: "none" });
const line = (x1: number, y1: number, x2: number, y2: number, strokeWidth = 2, stroke = COLOR): Shape => ({ kind: "line", x1, y1, x2, y2, stroke, strokeWidth });
const rect = (x: number, y: number, width: number, height: number, rx = 0): Shape => ({ kind: "rect", x, y, width, height, rx, fill: "none", stroke: COLOR, strokeWidth: 2 });

const bubbleOutline: Shape[] = [
  path("M20 11.5c0 4.14-3.58 7.5-8 7.5-1.12 0-2.18-.2-3.13-.56L4 20l1.1-4.07A7.13 7.13 0 0 1 4 11.5C4 7.36 7.58 4 12 4s8 3.36 8 7.5Z"),
];
const bubbleDots: Shape[] = [filledCircle(8.5, 11.5, 0.8), filledCircle(12, 11.5, 0.8), filledCircle(15.5, 11.5, 0.8)];

const ICON_DEFINITIONS: Record<string, IconDefinition> = {
  home: { shapes: [filledPath("M3 10 12 3l9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z")] },
  "home-outline": { shapes: [path("M3 10 12 3l9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z")] },
  "chatbubble-ellipses": { shapes: [filledPath("M20 11.5c0 4.14-3.58 7.5-8 7.5-1.12 0-2.18-.2-3.13-.56L4 20l1.1-4.07A7.13 7.13 0 0 1 4 11.5C4 7.36 7.58 4 12 4s8 3.36 8 7.5Z"), ...bubbleDots.map((shape) => ({ ...shape, fill: "#ffffff" }))] },
  "chatbubble-ellipses-outline": { shapes: [...bubbleOutline, ...bubbleDots] },
  "chatbox-ellipses-outline": { shapes: [...bubbleOutline, ...bubbleDots] },
  "chatbubble-outline": { shapes: bubbleOutline },
  "chatbubbles-outline": {
    shapes: [
      path("M16 17.5c-.98.32-2.04.5-3.15.5-4.34 0-7.85-2.88-7.85-6.45S8.51 5.1 12.85 5.1c3.85 0 7.05 2.25 7.7 5.25"),
      path("M17.15 9.1c2.68 0 4.85 1.92 4.85 4.3 0 1.01-.4 1.93-1.08 2.66L22 19l-2.86-.7c-.61.25-1.28.4-1.99.4-2.68 0-4.85-1.92-4.85-4.3"),
    ],
  },
  add: { shapes: [line(12, 5, 12, 19), line(5, 12, 19, 12)] },
  "add-circle": { shapes: [filledCircle(12, 12, 9), line(12, 7.5, 12, 16.5, 2, "#ffffff"), line(7.5, 12, 16.5, 12, 2, "#ffffff")] },
  "add-circle-outline": { shapes: [circle(12, 12, 9), line(12, 7.5, 12, 16.5), line(7.5, 12, 16.5, 12)] },
  search: { shapes: [circle(11, 11, 7.5), path("m20 20-4.3-4.3")] },
  "search-outline": { shapes: [circle(11, 11, 7.5), path("m20 20-4.3-4.3")] },
  person: { shapes: [filledPath("M12 12a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z")] },
  "person-outline": { shapes: [circle(12, 7.5, 3.8), path("M5 21a7 7 0 0 1 14 0")] },
  settings: {
    shapes: [
      path("M12 3.2l1.2 2.3 2.5.6 2.1-1.3 1.4 1.4-1.3 2.1.6 2.5 2.3 1.2v2l-2.3 1.2-.6 2.5 1.3 2.1-1.4 1.4-2.1-1.3-2.5.6-1.2 2.3h-2l-1.2-2.3-2.5-.6-2.1 1.3-1.4-1.4 1.3-2.1-.6-2.5L1.2 14v-2l2.3-1.2.6-2.5-1.3-2.1 1.4-1.4 2.1 1.3 2.5-.6 1.2-2.3Z"),
      circle(12, 13, 3.2),
    ],
  },
  "settings-outline": {
    shapes: [
      path("M12 3.2l1.2 2.3 2.5.6 2.1-1.3 1.4 1.4-1.3 2.1.6 2.5 2.3 1.2v2l-2.3 1.2-.6 2.5 1.3 2.1-1.4 1.4-2.1-1.3-2.5.6-1.2 2.3h-2l-1.2-2.3-2.5-.6-2.1 1.3-1.4-1.4 1.3-2.1-.6-2.5L1.2 14v-2l2.3-1.2.6-2.5-1.3-2.1 1.4-1.4 2.1 1.3 2.5-.6 1.2-2.3Z"),
      circle(12, 13, 3.2),
    ],
  },
  "checkmark": { shapes: [path("m5 12 4 4L19 6")] },
  "checkmark-circle": { shapes: [circle(12, 12, 9), path("m7.5 12 3 3 6-6")] },
  "chevron-back": { shapes: [path("m15 5-7 7 7 7")] },
  "chevron-forward": { shapes: [path("m9 5 7 7-7 7")] },
  "chevron-down": { shapes: [path("m5 9 7 7 7-7")] },
  "ellipsis-horizontal": { shapes: [filledCircle(6, 12, 1.4), filledCircle(12, 12, 1.4), filledCircle(18, 12, 1.4)] },
  "ellipsis-vertical": { shapes: [filledCircle(12, 6, 1.4), filledCircle(12, 12, 1.4), filledCircle(12, 18, 1.4)] },
  "create-outline": { shapes: [path("M4 20h4l10.5-10.5a2.12 2.12 0 0 0-3-3L5 17v3Z"), path("m14.5 7.5 2 2")] },
  close: { shapes: [line(5, 5, 19, 19), line(19, 5, 5, 19)] },
  "close-circle": { shapes: [filledCircle(12, 12, 9), line(8, 8, 16, 16, 2, "#ffffff"), line(16, 8, 8, 16, 2, "#ffffff")] },
  "document-text-outline": { shapes: [path("M6 3h8l4 4v14H6V3Z"), path("M14 3v5h5"), line(9, 12, 15, 12), line(9, 16, 15, 16)] },
  "trash-outline": { shapes: [path("M5 7h14"), path("M9 7V4h6v3"), path("M7 7l1 13h8l1-13"), line(10, 11, 10, 17), line(14, 11, 14, 17)] },
  heart: { shapes: [filledPath("M20.8 8.9c0 5.4-8.8 10.1-8.8 10.1S3.2 14.3 3.2 8.9A4.7 4.7 0 0 1 12 5.6a4.7 4.7 0 0 1 8.8 3.3Z")] },
  "heart-outline": { shapes: [path("M20.8 8.9c0 5.4-8.8 10.1-8.8 10.1S3.2 14.3 3.2 8.9A4.7 4.7 0 0 1 12 5.6a4.7 4.7 0 0 1 8.8 3.3Z")] },
  "paper-plane-outline": { shapes: [path("m22 2-7 20-4-9-9-4 20-7Z"), path("M22 2 11 13")] },
  "megaphone-outline": { shapes: [path("m4 13 12-5v8L4 13Z"), path("M16 8l4-2v12l-4-2"), line(7, 14, 8, 19)] },
  "open-outline": { shapes: [path("M14 3h7v7"), path("M21 3 10 14"), path("M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6")] },
  "cloud-offline-outline": { shapes: [path("M7 18h10a4 4 0 0 0 .8-7.92A6 6 0 0 0 6.2 9.2"), line(4, 4, 20, 20)] },
  "refresh-outline": { shapes: [path("M20 11a8 8 0 0 0-14.7-4L3 10"), path("M3 5v5h5"), path("M4 13a8 8 0 0 0 14.7 4L21 14"), path("M21 19v-5h-5")] },
  "images-outline": { shapes: [rect(3, 5, 15, 14, 2), rect(7, 3, 14, 14, 2), path("m7 15 3-3 2 2 2-2 4 4")] },
  "camera-outline": { shapes: [path("M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a1 1 0 0 1 1-1Z"), circle(12, 13, 3.2)] },
  "notifications-outline": { shapes: [path("M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z"), path("M10 21h4")] },
  "person-add-outline": { shapes: [circle(9, 7.5, 3.8), path("M2 21a7 7 0 0 1 14 0"), line(18, 8, 18, 14), line(15, 11, 21, 11)] },
  "eye-off-outline": { shapes: [path("M3 3l18 18"), path("M10.6 10.6a2 2 0 0 0 2.8 2.8"), path("M9.9 5.2A10.5 10.5 0 0 1 12 5c5 0 8.5 4.5 9.5 7a16.4 16.4 0 0 1-3.1 4.6"), path("M6.1 6.1C4.4 7.4 3.2 9.3 2.5 12c.6 2.2 3.5 7 9.5 7 1 0 1.9-.2 2.7-.5")] },
  "cloud-upload-outline": { shapes: [path("M7 18h10a4 4 0 0 0 .8-7.92A6 6 0 0 0 6.2 9.2"), line(12, 18, 12, 10), path("m8.5 13.5 3.5-3.5 3.5 3.5")] },
  "lock-closed-outline": { shapes: [rect(5, 10, 14, 10, 2), path("M8 10V7a4 4 0 0 1 8 0v3"), line(12, 14, 12, 17)] },
  "globe-outline": { shapes: [circle(12, 12, 9), path("M3 12h18"), path("M12 3a14 14 0 0 1 0 18"), path("M12 3a14 14 0 0 0 0 18")] },
  "people-outline": { shapes: [circle(9, 8, 3), circle(17, 9, 2.5), path("M3 21a6 6 0 0 1 12 0"), path("M14 16a5 5 0 0 1 7 5")] },
  "shield-checkmark-outline": { shapes: [path("M12 3 20 6v5c0 5-3.3 8.5-8 10-4.7-1.5-8-5-8-10V6l8-3Z"), path("m8 12 2.5 2.5L16 9")] },
  "exit-outline": { shapes: [path("M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4"), line(3, 12, 15, 12), path("m10 7 5 5-5 5")] },
  "archive-outline": { shapes: [rect(4, 7, 16, 13, 2), path("M3 7h18l-2-4H5L3 7Z"), line(9, 12, 15, 12)] },
  "help-circle-outline": { shapes: [circle(12, 12, 9), path("M9.5 9a2.7 2.7 0 1 1 4.5 2c-1.2.8-2 1.3-2 3"), filledCircle(12, 17.5, 0.8)] },
};

function resolvePaint(paint: string | undefined, color: string) {
  return paint === COLOR ? color : paint;
}

function renderShape(shape: Shape, index: number, color: string) {
  const common = {
    key: index,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (shape.kind === "path") {
    return React.createElement("path", { ...common, d: shape.d, fill: resolvePaint(shape.fill, color), stroke: resolvePaint(shape.stroke, color), strokeWidth: shape.strokeWidth });
  }
  if (shape.kind === "circle") {
    return React.createElement("circle", { ...common, cx: shape.cx, cy: shape.cy, r: shape.r, fill: resolvePaint(shape.fill, color), stroke: resolvePaint(shape.stroke, color), strokeWidth: shape.strokeWidth });
  }
  if (shape.kind === "line") {
    return React.createElement("line", { ...common, x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2, stroke: resolvePaint(shape.stroke, color), strokeWidth: shape.strokeWidth });
  }
  return React.createElement("rect", { ...common, x: shape.x, y: shape.y, width: shape.width, height: shape.height, rx: shape.rx, fill: resolvePaint(shape.fill, color), stroke: resolvePaint(shape.stroke, color), strokeWidth: shape.strokeWidth });
}

export function Ionicons({ name, size = 24, color = "currentColor", style, accessible, accessibilityLabel, accessibilityRole, testID, ...rest }: IoniconsProps) {
  const definition = ICON_DEFINITIONS[name] ?? ICON_DEFINITIONS["help-circle-outline"];
  const svgProps = {
    ...(rest as Record<string, unknown>),
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    color,
    style,
    role: accessible === false ? undefined : accessibilityRole ?? (accessibilityLabel ? "img" : undefined),
    "aria-hidden": accessible === false ? true : undefined,
    "aria-label": accessible === false ? undefined : accessibilityLabel,
    "data-testid": testID,
  };

  return React.createElement("svg", svgProps, definition.shapes.map((shape, index) => renderShape(shape, index, color)));
}

export default Ionicons;
